import classNames from 'classnames';
import { Flex } from '../Flex';
import * as styles from './index.css';

interface PaginationProps {
    currentPage: number;
    lastPage: number;
    onPageChange: (page: number) => void;
}

export function Pagination({ currentPage, lastPage, onPageChange }: PaginationProps) {
    if (lastPage <= 1) {
        return null;
    }

    const pageNumbers = getPageNumbers(currentPage, lastPage);

    return (
        <Flex as="nav" align="center" gap="1">
            <button
                type="button"
                className={styles.button}
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
            >
                前
            </button>

            {pageNumbers.map((page, index) =>
                page === '...' ? (
                    // biome-ignore lint/suspicious/noArrayIndexKey: For sake of simplicity
                    <span key={`ellipsis-${index}`} className={styles.ellipsis}>
                        ...
                    </span>
                ) : (
                    <button
                        key={page}
                        type="button"
                        className={classNames(styles.button, page === currentPage ? styles.active : '')}
                        onClick={() => onPageChange(page)}
                    >
                        {page}
                    </button>
                )
            )}

            <button
                type="button"
                className={styles.button}
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === lastPage}
            >
                次
            </button>
        </Flex>
    );
}

function getPageNumbers(current: number, last: number): (number | '...')[] {
    const maxVisible = 5;

    if (last <= maxVisible) {
        return Array.from({ length: last }, (_, i) => i + 1);
    }

    const pages: (number | '...')[] = [];

    if (current <= 3) {
        // Near the start
        for (let i = 1; i <= 4; i++) {
            pages.push(i);
        }
        pages.push('...');
        pages.push(last);
    } else if (current >= last - 2) {
        // Near the end
        pages.push(1);
        pages.push('...');
        for (let i = last - 3; i <= last; i++) {
            pages.push(i);
        }
    } else {
        // In the middle
        pages.push(1);
        pages.push('...');
        pages.push(current - 1);
        pages.push(current);
        pages.push(current + 1);
        pages.push('...');
        pages.push(last);
    }

    return pages;
}
